import * as React from "react";
import {
  DefaultButton,
  getId,
  Icon,
  IMessageBarStyles,
  IPanel,
  IPanelProps,
  IPanelStyles,
  IProcessedStyleSet,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  registerIcons,
} from "@fluentui/react";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { uniqueGUID } from "../../helpers/util";
import { Status, StatusProps } from "../Status/Status";
import { CircleCheckmarkSolid, CircleMultiplySolid, Warning } from "../Svg/icons";
import { DotsJustification, InProgressIndicator } from "./InProgressIndicator";

export enum AnchoredPanelType {
  SMALL,
  MEDIUM,
  LARGE,
  FULL_WIDTH,
  CUSTOM_WIDTH,
}

export enum ButtonType {
  PRIMARY,
  DEFAULT,
}

export enum MessageType {
  IN_PROGRESS,
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
}

export interface AnchoredPanelComponent {
  /**
   * Method when  the AnchoredPanel is set to be open
   */
  open: () => void;
  /**
   * Method when the AnchoredPanel is set to be closed
   */
  close: () => void;
  /**
   * Method to show/hide in-progress indicator above the panel's header
   * The in-progress indicator is off (hidden) at mount time
   */
  toggleInProgress: (on: boolean) => void;
}

export interface PanelFooterButton {
  /**
   * Label for the PanelFooter
   */
  label: string;
  /**
   * Button type for the PanelFooter
   */
  type?: ButtonType;
  /**
   * Callback when the PanelFooter button is clicked
   */
  onClick: () => void;
  /**
   * Disable the PanelFooter
   */
  disabled?: boolean;
  /**
   * Style for the PanelFooter
   */
  style?: React.CSSProperties;
  /**
   * data-test-id HTML attribute value
   */
  testId?: string;
}

export interface PanelMessage {
  /**
   * Message type to decide which icon to use
   */
  type: MessageType,
  /**
   * Text to render as message content
   */
  text: string | JSX.Element;
  /**
   * React component to render as message details
   */
  details?: JSX.Element;
  /**
   * If provided a dismiss button will be shown and the callback will be invoked if clicked on
   */
  onDismiss?: () => void;
}

export interface AnchoredPanelProps {
  /**
   * Component reference for the AnchoredPanel
   */
  componentRef?: (ref: AnchoredPanelComponent) => void;
  /**
   * The id of the element to which the panel will be anchored
   */
  anchorId: string;
  /**
   * Panel type for the AnchoredPanel
   */
  type?: AnchoredPanelType;
  /**
   * Custom width for the AnchoredPanel
   */
  customWidth?: number;
  /**
   * Title for the AnchoredPanel
   */
  title: string;
  /**
   * Status icon with tooltip to render to the right of the title
   */
  status?: Pick<StatusProps, "status" | "tooltip">;
  /**
   * Subtitle for the AnchoredPanel
   */
  subTitle?: string;
  /**
   * Icon name for the AnchoredPanel
   */
  icon?: string;
  /**
   * Option to open the AnchoredPanel
   */
  isOpen?: boolean;
  /**
   * Specify if the AnchoredPanel is blocked
   */
  isBlocking?: boolean;
  /**
   * Callback when the AnchoredPanel is closed
   */
  onClose?: () => void;
  /**
   * Footer button on the AnchoredPanel
   */
  footer?: PanelFooterButton[];
  /**
   * Message to display on the AnchoredPanel
   */
  message?: PanelMessage;
  /**
   * Components displayed as part of the AnchoredPanel
   */
  children: React.ReactNode;
  /**
   * A test id for the anchored panel
   */
  testId?: string;
}

const basePanelStyles: Partial<IPanelStyles> = {
  navigation: {
    flexDirection: "column",
    justifyContent: "unset",
    height: "unset",
  },
  headerText: {
    fontSize: "24px",
    lineHeight: "28px",
    height: "32px",
  },
  content: {
    padding: "20px 20px 0px 20px",
    overflowX: "hidden",
    overflowY: "auto",
    flex: "1 1 100%",
  },
  scrollableContent: {
    height: "100%",
    display: "flex",
    flexDirection: "inherit",
  },
  footer: {
    borderTop: "1px solid",
    borderColor: "#bbb",
  },
  footerInner: { padding: 20 },
  subComponentStyles: {
    closeButton: {
      root: {
        height: "32px",
        width: "32px",
      },
      rootHovered: {
        backgroundColor: "rgb(186, 20, 26)",
        color: "rgb(255, 255, 255)",
      },
      rootPressed: {
        backgroundColor: "rgb(186, 20, 26)",
        color: "rgb(255, 255, 255)",
      },
    },
  },
};

const fullWidthPanelStyles: Partial<IPanelStyles> = { main: { boxShadow: "unset" } };

const messageBarStyles: Partial<IMessageBarStyles> = {
  root: {
    margin: "0px 20px",
    width: "unset",
  },
  content: {
    height: "50px",
    alignItems: "center",
  },
};

const messageDetailsBarStyles: Partial<IMessageBarStyles> = { actions: { flexDirection: "row" } };

const barBackgroundError: IMessageBarStyles = {
  root: {
    backgroundColor: "#fef0f1",
    borderColor: "#fef0f1",
  },
};
const barBackgroundWarning: IMessageBarStyles = {
  root: {
    backgroundColor: "#fff8f0",
    borderColor: "#fff8f0",
  },
};
const barBackgroundSuccess: IMessageBarStyles = {
  root: {
    backgroundColor: "#f8fff0",
    borderColor: "#f8fff0",
  },
};

const getPanelType = (type?: AnchoredPanelType): PanelType => {
  switch (type) {
    case AnchoredPanelType.CUSTOM_WIDTH:
      return PanelType.custom;
    case AnchoredPanelType.SMALL:
      return PanelType.smallFixedFar;
    case AnchoredPanelType.MEDIUM:
      return PanelType.medium;
    case AnchoredPanelType.LARGE:
      return PanelType.large;
    case AnchoredPanelType.FULL_WIDTH:
    default:
      return PanelType.smallFluid;
  }
};

export interface AnchoredPanelTestIds extends ComponentTestIds{
  /**
   * The header containing the title, the vaidation message and the close button
   */
  header: string;
}

export const buildAnchoredPanelTestIds = (baseTestId?: string): AnchoredPanelTestIds => ({
  component: buildTestId(baseTestId),
  header: buildTestId(baseTestId, "-header"),
});

registerIcons({
  icons: {
    "error-svg": CircleMultiplySolid,
    "success-svg": CircleCheckmarkSolid,
    "warning-svg": Warning,
  },
});

/**
 * The AnchoredPanel renders the components provided by the caller within the panel
 * based on the specified width with a header and a footer.
 */

export const AnchoredPanel = (
  {
    componentRef,
    anchorId,
    title,
    status,
    subTitle,
    icon,
    type = AnchoredPanelType.FULL_WIDTH,
    customWidth,
    isOpen,
    isBlocking = false,
    onClose,
    footer,
    message,
    children,
    testId,
  }: AnchoredPanelProps,
): JSX.Element => {
  const panelRef = React.useRef<IPanel>(null);
  const thisRef = React.useRef<AnchoredPanelComponent>();
  const [showInProgress, setShowInProgress] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (componentRef) {
      thisRef.current = {
        open: () => panelRef.current?.open(),
        close: () => panelRef.current?.dismiss(),
        toggleInProgress: (on: boolean) => setShowInProgress(on),
      } as AnchoredPanelComponent;
      componentRef(thisRef.current);
    }
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRenderDummyHeader = (): JSX.Element => (<div />);

  const renderHeader = (
    props: IPanelProps,
  ): JSX.Element | null => {
    const { headerText, headerTextProps = {} } = props;
    const headerTextId = headerText && `${getId("Panel")}-headerText`;

    if (headerText) {
      return (
        <div style={{ flexGrow: 1, alignSelf: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            {icon && <Icon iconName={icon} />}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "inherit" }}>
                <div
                  id={headerTextId}
                  role="heading"
                  aria-level={1}
                  {...headerTextProps}
                  style={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "rgb(50, 49, 48)",
                    lineHeight: "28px",
                    height: "32px",
                    marginRight: "10px",
                  }}
                >
                  {headerText}
                </div>
                {status && (
                  <span style={{ alignSelf: "center" }}>
                    <Status
                      status={status.status}
                      tooltip={status.tooltip}
                    />
                  </span>
                )}
              </div>
              {subTitle && (
                <span
                  key="subtitle"
                  style={{ fontSize: "12px", lineHeight: "14px", color: "#646464" }}
                >
                  {subTitle}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const getMessageBarData = (messageType: MessageType):
  { type: MessageBarType, iconName: string, styles: IMessageBarStyles } => {
    switch (messageType) {
      case MessageType.IN_PROGRESS:
        return {
          type: MessageBarType.info,
          iconName: "info",
          styles: mergeStyleSets(messageBarStyles),
        };
      case MessageType.ERROR:
        return {
          type: MessageBarType.error,
          iconName: "error-svg",
          styles: mergeStyleSets(
            messageBarStyles,
            barBackgroundError,
          ),
        };
      case MessageType.WARNING:
        return {
          type: MessageBarType.warning,
          iconName: "warning-svg",
          styles: mergeStyleSets(
            messageBarStyles,
            barBackgroundWarning,
          ),
        };
      case MessageType.SUCCESS:
        return {
          type: MessageBarType.success,
          iconName: "success-svg",
          styles: mergeStyleSets(
            messageBarStyles,
            barBackgroundSuccess,
          ),
        };
      case MessageType.INFO:
      default:
        return { type: MessageBarType.info, iconName: "info", styles: messageBarStyles };
    }
  };

  const onRenderNavigation = (
    props?: IPanelProps,
    defaultRender?: (props?: IPanelProps) => JSX.Element | null,
  ): JSX.Element => {
    const navigationNode = defaultRender?.(props);
    const messageBarNode = message && (
      <MessageBar
        delayedRender={false}
        styles={
          message.details
            ? mergeStyleSets(getMessageBarData(message.type).styles, messageDetailsBarStyles)
            : getMessageBarData(message.type).styles
        }
        messageBarIconProps={{ iconName: getMessageBarData(message.type).iconName }}
        onDismiss={message.onDismiss ? () => message.onDismiss?.() : undefined}
        actions={message.details}
      >
        {
          message.type === MessageType.IN_PROGRESS
            ? (
              <div style={{ minWidth: "500px" }}>
                <div style={{ display: "inline-flex" }}>{message.text}</div>
                <InProgressIndicator justification={DotsJustification.LEFT} />
              </div>
            )
            : message.text
          }
      </MessageBar>
    );
    const navigationNodeChildren = (
      <>
        {showInProgress && <InProgressIndicator justification={DotsJustification.CENTER} />}
        <div
          data-test-id={buildAnchoredPanelTestIds(testId).header}
          id="panel-navigation"
          style={{ display: "flex", justifyContent: "flex-end", padding: "8px 20px" }}
        >
          {renderHeader(props || {})}
          {React.Children.toArray(navigationNode?.props.children)}
        </div>
        {messageBarNode}
      </>
    );
    return navigationNode
      ? React.cloneElement(navigationNode, { ...navigationNode.props, children: navigationNodeChildren })
      : (<div />);
  };

  const onRenderFooterContent = (): JSX.Element | null => {
    if (footer && footer.length > 0) {
      return (
        <div>
          {footer?.map(button => {
            if (button.type === ButtonType.PRIMARY) {
              return (
                <PrimaryButton
                  key={uniqueGUID()}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  style={button.style}
                  data-test-id={button.testId}
                >
                  {button.label}
                </PrimaryButton>
              );
            }
            return (
              <DefaultButton
                key={uniqueGUID()}
                onClick={button.onClick}
                disabled={button.disabled}
                style={button.style}
                data-test-id={button.testId}
              >
                {button.label}
              </DefaultButton>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const getPanelStyles = (): IProcessedStyleSet<Partial<IPanelStyles>> => {
    switch (type) {
      case AnchoredPanelType.FULL_WIDTH:
        return mergeStyleSets(basePanelStyles, fullWidthPanelStyles);
      default:
        return mergeStyleSets(basePanelStyles);
    }
  };

  return (
    <Panel
      componentRef={panelRef}
      isOpen={isOpen}
      isBlocking={isBlocking}
      type={getPanelType(type)}
      customWidth={type === AnchoredPanelType.CUSTOM_WIDTH && customWidth ? `${customWidth}px` : undefined}
      headerText={title}
      onRenderHeader={onRenderDummyHeader}
      onRenderNavigation={onRenderNavigation}
      onRenderFooterContent={footer && footer.length > 0 ? onRenderFooterContent : undefined}
      isFooterAtBottom={footer && footer.length > 0}
      onDismiss={onClose}
      styles={getPanelStyles()}
      layerProps={{ hostId: anchorId }}
    >
      {children}
    </Panel>
  );
};
